import { Check, CheckSquare, Mail, MailOpen, Square, Trash2, Archive, FolderInput, Ban, X, Flame, ShieldCheck, Loader2, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { useMoveToFolderStore } from '@/stores/moveToFolderStore';
import { motion, AnimatePresence } from 'motion/react';
import { slideUp } from '@/lib/animations';

interface BulkActionBarProps {
  canApproveSenders: boolean;
  canBlockSenders: boolean;
  allVisibleSelected: boolean;
  onSelectAllVisible: () => void;
}

export function BulkActionBar({
  canApproveSenders,
  canBlockSenders,
  allVisibleSelected,
  onSelectAllVisible,
}: BulkActionBarProps) {
  const bulkSelection = useBulkOperationsStore((state) => state.bulkSelection);
  const applyBulkAction = useBulkOperationsStore((state) => state.applyBulkAction);
  const bulkStatus = useBulkOperationsStore((state) => state.bulkActionStatus);
  const isBulkEditMode = useBulkOperationsStore((state) => state.bulkEditMode);
  const setBulkEditMode = useBulkOperationsStore((state) => state.setBulkEditMode);

  // Round 4: the bar is the bulk mode — visible while the mode is on (even at
  // zero selected, so Done stays reachable) or while anything is selected.
  const barVisible = isBulkEditMode || bulkSelection.size > 0;
  const bulkSelectionCount = bulkSelection.size;

  // Done: exits the mode (which also clears the selection).
  const handleDone = () => {
    setBulkEditMode(false);
  };
  const trainingInProgress =
    bulkStatus?.status === 'running'
    && (bulkStatus.action === 'trainSpam' || bulkStatus.action === 'trainGood');

  const hasSecondaryActions = canApproveSenders || canBlockSenders;

  return (
    <AnimatePresence>
      {barVisible && (
        <motion.div
          {...slideUp}
          // Mobile: takes over the dock's slot flush at the bottom (the dock
          // yields while bulk mode is on); desktop: floats near the bottom
          // edge as before.
          className="fixed bottom-0 inset-x-0 rounded-none px-2 pt-1.5 pb-[max(env(safe-area-inset-bottom,0px),0.375rem)] md:bottom-6 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:rounded-full md:py-1.5 z-50 surface-dark shadow-2xl"
        >
          <div className="flex items-center justify-evenly gap-1 md:justify-start">
            {/* Selection controls */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-11 md:h-9 px-2.5 md:px-2 gap-1.5 rounded-full"
                  onClick={onSelectAllVisible}
                >
                  {allVisibleSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  <span className="font-medium tabular-nums">{bulkSelectionCount}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Select all visible</TooltipContent>
            </Tooltip>

            {/* Training status indicator */}
            {bulkStatus && (bulkStatus.action === 'trainSpam' || bulkStatus.action === 'trainGood') && (
              <div className="hidden sm:flex items-center gap-1 rounded-full border border-border/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {bulkStatus.status === 'running' ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Check className="h-3 w-3 text-success-ink" />
                )}
                <span>
                  {bulkStatus.completed}/{bulkStatus.total}
                </span>
              </div>
            )}

            <div className="w-px h-5 bg-border/50 mx-1" />

            {/* Primary actions — canonical order shared with every other
                action surface: archive, delete, read/unread, then move */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => applyBulkAction('archive')}>
                  <Archive className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full hover:bg-destructive/10 hover:text-destructive" onClick={() => applyBulkAction('delete')}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>

            {/* Desktop: show read/unread inline */}
            <div className="hidden sm:flex items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => applyBulkAction('markRead')}>
                    <MailOpen className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark read</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => applyBulkAction('markUnread')}>
                    <Mail className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark unread</TooltipContent>
              </Tooltip>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full hover:bg-primary/10 hover:text-primary" onClick={() => useMoveToFolderStore.getState().open(Array.from(bulkSelection))}>
                  <FolderInput className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Move to folder</TooltipContent>
            </Tooltip>

            {/* Overflow menu for secondary actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full hover:bg-muted/50" aria-label="More actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="mb-2">
                {/* Mobile-only: read/unread in menu */}
                <div className="sm:hidden">
                  <DropdownMenuItem onClick={() => applyBulkAction('markRead')} className="gap-2">
                    <MailOpen className="h-4 w-4" />
                    <span>Mark read</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => applyBulkAction('markUnread')} className="gap-2">
                    <Mail className="h-4 w-4" />
                    <span>Mark unread</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </div>

                {hasSecondaryActions && (
                  <>
                    {canApproveSenders && (
                      <DropdownMenuItem onClick={() => applyBulkAction('approveSender')} className="gap-2">
                        <Check className="h-4 w-4" />
                        <span>Approve senders</span>
                      </DropdownMenuItem>
                    )}
                    {canBlockSenders && (
                      <DropdownMenuItem onClick={() => applyBulkAction('blockSender')} className="gap-2">
                        <Ban className="h-4 w-4" />
                        <span>Block senders</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}

                <DropdownMenuItem
                  onClick={() => applyBulkAction('trainSpam')}
                  disabled={trainingInProgress}
                  className="gap-2"
                >
                  <Flame className="h-4 w-4" />
                  <span>Train as spam</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => applyBulkAction('trainGood')}
                  disabled={trainingInProgress}
                  className="gap-2"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span>Train as good</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="w-px h-5 bg-border/50 mx-1" />

            {/* Done — exits selection mode */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9 rounded-full" onClick={handleDone} aria-label="Done">
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Done</TooltipContent>
            </Tooltip>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
